import { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Loader2, Upload, Trash2, ImagePlus } from 'lucide-react';
import api from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const ALLOWED_PHOTO_EXT = ['jpg', 'jpeg', 'png', 'webp'];
const MAX_PHOTO_SIZE = 5 * 1024 * 1024;

const CATEGORY_OPTIONS = [
  { value: 'automate', label: 'Automate' },
  { value: 'variateur', label: 'Variateur' },
  { value: 'verin_pneumatique', label: 'Vérin Pneumatique' },
  { value: 'vapeur', label: 'Vapeur' },
  { value: 'relais_securite', label: 'Relais de sécurité + Capteur' },
  { value: 'ecran_siemens', label: 'Ecran SIEMENS' },
  { value: 'hydrolique', label: 'Hydrolique' },
  { value: 'pneumatique', label: 'Pneumatique' },
  { value: 'encodeur_occasion', label: 'Encodeur occasion' },
  { value: 'instrument', label: 'Instrument' },
  { value: 'compteur', label: 'Compteur' },
  { value: 'capteur', label: 'Capteur' },
];

const STATE_OPTIONS = [
  { value: 'neuf', label: 'Neuf' },
  { value: 'occasion', label: 'Occasion' },
  { value: 'obsolete', label: 'Obsolète' },
];

const STATUS_OPTIONS = [
  { value: 'en_stock', label: 'En stock' },
  { value: 'rupture', label: 'Rupture' },
  { value: 'sur_commande', label: 'Sur commande' },
];

const EMPTY_FORM = {
  reference: '', name: '', category: '', quantity: 0, stock_minimum: 5,
  purchase_price: 0, sale_price: 0, supplier: '', location: '',
  brand: '', description: '', state: 'neuf', status: 'en_stock',
  visible_on_site: true, featured_on_site: false, photo_url: '',
};

export default function ProductForm({ open, onClose, product, defaultCategory, onSaved }) {
  const isEdit = !!product;
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [photoVersion, setPhotoVersion] = useState(0);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (product) {
      setForm({
        reference: product.reference || '',
        name: product.name || '',
        category: product.category || defaultCategory || '',
        quantity: product.quantity ?? 0,
        stock_minimum: product.stock_minimum ?? 5,
        purchase_price: product.purchase_price ?? 0,
        sale_price: product.sale_price ?? 0,
        supplier: product.supplier || '',
        location: product.location || '',
        brand: product.brand || '',
        description: product.description || '',
        state: product.state || 'neuf',
        status: product.status || 'en_stock',
        visible_on_site: product.visible_on_site ?? false,
        featured_on_site: product.featured_on_site ?? false,
        photo_url: product.photo_url || '',
      });
    } else {
      setForm({ ...EMPTY_FORM, category: defaultCategory || '' });
    }
    setError('');
    setPhotoError('');
    setPhotoVersion(0);
  }, [product, defaultCategory, open]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const validatePhotoFile = (file) => {
    if (!file) return 'Aucun fichier sélectionné';
    const ext = file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : '';
    if (!ALLOWED_PHOTO_EXT.includes(ext)) {
      return 'Format non supporté (JPG, PNG, WEBP uniquement)';
    }
    if (file.size > MAX_PHOTO_SIZE) {
      return 'Fichier trop gros (max 5 MB)';
    }
    return null;
  };

  const handlePhotoUpload = async (file) => {
    if (!product?.id) return;
    setPhotoError('');
    const err = validatePhotoFile(file);
    if (err) { setPhotoError(err); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/admin/products/${product.id}/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      handleChange('photo_url', data.photo_url || '');
      setPhotoVersion(Date.now());
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setPhotoError(typeof detail === 'string' ? detail : "Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoDelete = async () => {
    if (!product?.id) return;
    setUploading(true);
    setPhotoError('');
    try {
      await api.delete(`/admin/products/${product.id}/photo`);
      handleChange('photo_url', '');
      setPhotoVersion(Date.now());
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setPhotoError(typeof detail === 'string' ? detail : 'Erreur lors de la suppression');
    } finally {
      setUploading(false);
      setConfirmDeleteOpen(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading || !product?.id) return;
    const file = e.dataTransfer.files?.[0];
    if (file) handlePhotoUpload(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.reference.trim()) {
      setError('La référence est obligatoire');
      return;
    }
    if (!form.name.trim()) {
      setError('Le nom est obligatoire');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/products/${product.id}`, form);
      } else {
        await api.post('/products', form);
      }
      onSaved();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-slate-900" data-testid="product-form-title">
            {isEdit ? 'Modifier le produit' : 'Nouveau produit'}
          </DialogTitle>
        </DialogHeader>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" data-testid="product-form-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Section Photo du produit (en haut) */}
          <div className="border border-slate-200 rounded-md p-4 bg-slate-50/50" data-testid="product-photo-section">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-semibold text-slate-700">Photo du produit</Label>
              {isEdit && form.photo_url && !uploading && (
                <span className="text-xs text-slate-500">Glissez-déposez pour remplacer</span>
              )}
            </div>

            {!isEdit && (
              <div className="text-sm text-slate-500 italic py-6 text-center bg-white border border-dashed border-slate-300 rounded" data-testid="photo-disabled-msg">
                Enregistrez d'abord le produit pour ajouter une photo
              </div>
            )}

            {isEdit && form.photo_url && (
              <div className="flex items-start gap-4" data-testid="photo-preview-wrap">
                <div
                  className="relative w-[200px] h-[200px] rounded-md border border-slate-200 overflow-hidden bg-white flex-shrink-0"
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                >
                  <img
                    src={`${BACKEND_URL}${form.photo_url}${photoVersion ? `?v=${photoVersion}` : ''}`}
                    alt="Photo produit"
                    className="w-full h-full object-contain"
                    data-testid="product-photo-preview"
                  />
                  {uploading && (
                    <div className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center gap-2">
                      <Loader2 className="w-6 h-6 animate-spin text-[#0A3D73]" />
                      <span className="text-xs text-slate-600">Compression et envoi...</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 flex flex-col gap-2">
                  <Button
                    type="button" variant="outline" disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                    className="border-slate-300 justify-start"
                    data-testid="photo-replace-btn"
                  >
                    <ImagePlus className="w-4 h-4 mr-2" /> Remplacer
                  </Button>
                  <Button
                    type="button" variant="outline" disabled={uploading}
                    onClick={() => setConfirmDeleteOpen(true)}
                    className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 justify-start"
                    data-testid="photo-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                  </Button>
                  <p className="text-xs text-slate-500 mt-1">JPG, PNG, WEBP · max 5 MB</p>
                </div>
              </div>
            )}

            {isEdit && !form.photo_url && (
              <div
                onClick={() => !uploading && fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                className={`cursor-pointer rounded-md border-2 border-dashed py-8 text-center transition-colors ${dragOver ? 'border-[#0A3D73] bg-blue-50' : 'border-slate-300 bg-white hover:bg-slate-50'}`}
                data-testid="photo-dropzone"
              >
                {uploading ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-6 h-6 animate-spin text-[#0A3D73]" />
                    <span className="text-sm text-slate-600">Compression et envoi...</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-8 h-8 text-slate-400" />
                    <span className="text-sm font-medium text-slate-700">Cliquez ou glissez une photo ici</span>
                    <span className="text-xs text-slate-500">JPG, PNG, WEBP · max 5 MB</span>
                  </div>
                )}
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
              className="hidden"
              data-testid="photo-file-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handlePhotoUpload(file);
                e.target.value = '';
              }}
            />

            {photoError && (
              <div className="mt-2 text-sm text-red-600" data-testid="photo-error">{photoError}</div>
            )}
          </div>

          {/* Row 1: Reference + Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Référence *</Label>
              <Input
                value={form.reference}
                onChange={(e) => handleChange('reference', e.target.value)}
                placeholder="REF-001"
                className="border-slate-300"
                data-testid="product-reference-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Nom *</Label>
              <Input
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Nom du produit"
                className="border-slate-300"
                data-testid="product-name-input"
              />
            </div>
          </div>

          {/* Row 2: Category + Brand */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => handleChange('category', v)}>
                <SelectTrigger className="border-slate-300" data-testid="product-category-select">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Marque</Label>
              <Input
                value={form.brand}
                onChange={(e) => handleChange('brand', e.target.value)}
                placeholder="Ex: Siemens, Schneider..."
                className="border-slate-300"
                data-testid="product-brand-input"
              />
            </div>
          </div>

          {/* Row 3: Quantity + Stock minimum */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Quantité</Label>
              <Input
                type="number" min="0"
                value={form.quantity}
                onChange={(e) => handleChange('quantity', parseInt(e.target.value) || 0)}
                className="border-slate-300"
                data-testid="product-quantity-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Stock min.</Label>
              <Input
                type="number" min="0"
                value={form.stock_minimum}
                onChange={(e) => handleChange('stock_minimum', parseInt(e.target.value) || 0)}
                className="border-slate-300"
                data-testid="product-stock-min-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Prix achat</Label>
              <Input
                type="number" min="0" step="0.01"
                value={form.purchase_price}
                onChange={(e) => handleChange('purchase_price', parseFloat(e.target.value) || 0)}
                className="border-slate-300"
                data-testid="product-purchase-price-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Prix vente</Label>
              <Input
                type="number" min="0" step="0.01"
                value={form.sale_price}
                onChange={(e) => handleChange('sale_price', parseFloat(e.target.value) || 0)}
                className="border-slate-300"
                data-testid="product-sale-price-input"
              />
            </div>
          </div>

          {/* Row 4: Supplier + Location */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Fournisseur</Label>
              <Input
                value={form.supplier}
                onChange={(e) => handleChange('supplier', e.target.value)}
                placeholder="Nom du fournisseur"
                className="border-slate-300"
                data-testid="product-supplier-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Emplacement</Label>
              <Input
                value={form.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="Ex: Étagère A-12"
                className="border-slate-300"
                data-testid="product-location-input"
              />
            </div>
          </div>

          {/* Row 5: State + Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">État</Label>
              <Select value={form.state} onValueChange={(v) => handleChange('state', v)}>
                <SelectTrigger className="border-slate-300" data-testid="product-state-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Statut</Label>
              <Select value={form.status} onValueChange={(v) => handleChange('status', v)}>
                <SelectTrigger className="border-slate-300" data-testid="product-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 6: Description */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-slate-700">Description</Label>
            <textarea
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Description du produit..."
              rows={2}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A3D73]/50 focus:border-[#0A3D73] resize-none"
              data-testid="product-description-input"
            />
          </div>

          {/* Publication site web */}
          {isEdit && (
            <div className="border-t pt-3 mt-1">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Publication site web</p>
              <div className="flex flex-col gap-3">
                <label className="flex items-center gap-3 cursor-pointer" data-testid="toggle-visible-site">
                  <button type="button" onClick={() => handleChange('visible_on_site', !form.visible_on_site)}
                    className={`relative w-10 h-5 rounded-full transition-colors ${form.visible_on_site ? 'bg-orange-500' : 'bg-slate-300'}`}>
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${form.visible_on_site ? 'translate-x-5' : ''}`} />
                  </button>
                  <span className="text-sm text-slate-700">Visible sur le site web</span>
                </label>
                <label className={`flex items-center gap-3 ${form.visible_on_site ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`} data-testid="toggle-featured-site">
                  <button type="button" disabled={!form.visible_on_site} onClick={() => form.visible_on_site && handleChange('featured_on_site', !form.featured_on_site)}
                    className={`relative w-10 h-5 rounded-full transition-colors ${form.featured_on_site && form.visible_on_site ? 'bg-orange-500' : 'bg-slate-300'}`}>
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${form.featured_on_site && form.visible_on_site ? 'translate-x-5' : ''}`} />
                  </button>
                  <span className="text-sm text-slate-700">Mettre en vedette sur la home</span>
                </label>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-slate-300" data-testid="product-form-cancel">
              Annuler
            </Button>
            <Button
              type="submit" disabled={saving}
              className="bg-[#0A3D73] hover:bg-[#082E56] text-white"
              data-testid="product-form-submit"
            >
              {saving ? (
                <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Enregistrement...</span>
              ) : (
                isEdit ? 'Enregistrer' : 'Ajouter'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent data-testid="confirm-delete-photo-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la photo ?</AlertDialogTitle>
            <AlertDialogDescription>
              La photo de <span className="font-semibold">{product?.name || product?.reference || 'ce produit'}</span> sera définitivement supprimée. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={uploading} data-testid="confirm-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handlePhotoDelete(); }}
              disabled={uploading}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-confirm"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
